import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { MapPin, Phone, Mail, Globe, Clock, CheckCircle } from 'lucide-react';
import { PageHero } from '../components/ui/UI';
import { INQUIRY_SERVICES } from '../data/services';
import './Contact.css';

const INITIAL_FORM = {
  firstName: '', lastName: '', email: '', phone: '',
  country: '', city: '', service: '', message: '',
  howHeard: '',
};

export default function Contact() {
  const location = useLocation();
  const [form, setForm]     = useState(INITIAL_FORM);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  // Pre-fill service if navigated from a service card
  useEffect(() => {
    if (location.state?.service) {
      setForm(f => ({ ...f, service: location.state.service }));
    }
  }, [location.state]);

  const validate = () => {
    const e = {};
    if (!form.firstName.trim()) e.firstName = 'First name is required';
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) e.email = 'Valid email is required';
    if (!form.service) e.service = 'Please select a service';
    return e;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setSubmitted(true);
  };

  const handleReset = () => {
    setForm(INITIAL_FORM);
    setSubmitted(false);
    setErrors({});
  };

  // Determine dynamic hero title
  const heroTitle = form.service ? `Inquire about` : "Send Us an";
  const heroHighlight = form.service ? form.service : "Inquiry";

  return (
    <div className="page-wrapper">
      <PageHero
        badge="Contact Us"
        title={heroTitle}
        highlight={heroHighlight}
        subtitle="Fill in the form below and our team will respond within 2–4 hours. For urgent matters, call us directly at +919967765284."
      />

      <section className="section-pad">
        <div className="container">
          <div className="contact-grid">

            {/* Left – Info */}
            <div className="contact-info">
              <h3 className="contact-info__heading">Get in Touch</h3>
              <p className="contact-info__sub">
                We're here to help with any service at Mumbai bombay international worldwide - no matter how big or small the inquiry.
                Our multilingual team handles requests in English, Hindi, Marathi, and more.
              </p>

              <div className="contact-details">
                {[
                  { icon: <MapPin size={16} />, label: 'Address',      value: 'Mumbai bombay international worldwide ' },
                  { icon: <Phone size={16} />,  label: 'Phone / WhatsApp', value: '+919967765284 (Mobile & WhatsApp), +919136663456' },
                  { icon: <Mail size={16} />,   label: 'Email',        value: 'onlineworldwide11@gmail.com' },
                  { icon: <Globe size={16} />,  label: 'Website',      value: 'www.onlineworldwide.com' },
                  { icon: <Clock size={16} />,  label: 'Availability', value: '24/7 - 365 days a year' },
                ].map(item => (
                  <div key={item.label} className="contact-detail-item">
                    <div className="contact-detail-item__icon">{item.icon}</div>
                    <div>
                      <div className="contact-detail-item__label">{item.label}</div>
                      <div className="contact-detail-item__value">{item.value}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="contact-response-times">
                <h4 className="contact-response-times__title">Typical Response Times</h4>
                {[
                  { type: 'General Inquiry',       time: '2–4 hours' },
                  { type: 'Education / Admission',  time: 'Same day' },
                  { type: 'Job / Visa',             time: '24 hours' },
                  { type: 'Urgent / Emergency',     time: 'Call us' },
                ].map(r => (
                  <div key={r.type} className="contact-response-item">
                    <span className="contact-response-item__type">{r.type}</span>
                    <span className="contact-response-item__time">{r.time}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right – Form */}
            <div className="contact-form-wrap">
              {submitted ? (
                <div className="contact-success">
                  <div className="contact-success__icon">
                    <CheckCircle size={48} color="var(--teal)" />
                  </div>
                  <h3 className="contact-success__title">Inquiry Received!</h3>
                  <p className="contact-success__sub">
                    Thank you, <strong>{form.firstName}</strong>! We've received your inquiry 
                    about <strong>"{form.service}"</strong> in Mumbai bombay international worldwide and will get back to you at
                    <strong> {form.email}</strong> within 2–4 hours.
                  </p>
                  <div className="contact-success__details">
                    <div className="contact-success__detail">
                      <span>Name:</span> {form.firstName} {form.lastName}
                    </div>
                    <div className="contact-success__detail">
                      <span>Service:</span> {form.service}
                    </div>
                    {form.phone && (
                      <div className="contact-success__detail">
                        <span>Phone:</span> {form.phone}
                      </div>
                    )}
                    {form.country && (
                      <div className="contact-success__detail">
                        <span>Country:</span> {form.country}
                      </div>
                    )}
                  </div>
                  <button className="btn btn--outline" onClick={handleReset}>
                    Submit Another Inquiry
                  </button>
                </div>
              ) : (
                <form className="contact-form" onSubmit={handleSubmit} noValidate>
                  <div className="contact-form__title">Inquiry Form</div>

                  {/* Name row */}
                  <div className="form-row">
                    <div className={`form-field ${errors.firstName ? 'form-field--error' : ''}`}>
                      <label>First Name *</label>
                      <input
                        type="text" name="firstName" placeholder="Ravi"
                        value={form.firstName} onChange={handleChange}
                      />
                      {errors.firstName && <span className="form-error">{errors.firstName}</span>}
                    </div>
                    <div className="form-field">
                      <label>Last Name</label>
                      <input
                        type="text" name="lastName" placeholder="Sharma"
                        value={form.lastName} onChange={handleChange}
                      />
                    </div>
                  </div>

                  {/* Email + Phone */}
                  <div className="form-row">
                    <div className={`form-field ${errors.email ? 'form-field--error' : ''}`}>
                      <label>Email Address *</label>
                      <input
                        type="email" name="email" placeholder="you@example.com"
                        value={form.email} onChange={handleChange}
                      />
                      {errors.email && <span className="form-error">{errors.email}</span>}
                    </div>
                    <div className="form-field">
                      <label>Phone / WhatsApp</label>
                      <input
                        type="tel" name="phone" placeholder="+91 or international number"
                        value={form.phone} onChange={handleChange}
                      />
                    </div>
                  </div>

                  {/* Country + City */}
                  <div className="form-row">
                    <div className="form-field">
                      <label>Country</label>
                      <input
                        type="text" name="country" placeholder=" / UK / Singapore..."
                        value={form.country} onChange={handleChange}
                      />
                    </div>
                    <div className="form-field">
                      <label>City</label>
                      <input
                        type="text" name="city" placeholder="Mumbai bombay international worldwide / London..."
                        value={form.city} onChange={handleChange}
                      />
                    </div>
                  </div>

                  {/* Service */}
                  <div className={`form-field ${errors.service ? 'form-field--error' : ''}`}>
                    <label>Service of Interest *</label>
                    <select name="service" value={form.service} onChange={handleChange}>
                      <option value="">Select the service you're inquiring about...</option>
                      {INQUIRY_SERVICES.map(group => (
                        <optgroup key={group.group} label={group.group}>
                          {group.options.map(opt => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                    {errors.service && <span className="form-error">{errors.service}</span>}
                  </div>

                  {/* How heard */}
                  <div className="form-field">
                    <label>How did you hear about us?</label>
                    <select name="howHeard" value={form.howHeard} onChange={handleChange}>
                      <option value="">Select an option</option>
                      <option>Google Search</option>
                      <option>Social Media</option>
                      <option>Friend / Referral</option>
                      <option>WhatsApp</option>
                      <option>Other</option>
                    </select>
                  </div>

                  {/* Message */}
                  <div className="form-field">
                    <label>Message / Additional Details</label>
                    <textarea
                      name="message"
                      rows={4}
                      placeholder="Tell us more about what you need at Mumbai bombay international worldwide - dates, budget, specific requirements, etc."
                      value={form.message}
                      onChange={handleChange}
                    />
                  </div>

                  <button type="submit" className="btn btn--primary contact-form__submit">
                    Send Inquiry →
                  </button>

                  <p className="contact-form__note">
                    * Required fields. We respond within 2–4 hours. Your information is kept private and never shared.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ strip */}
      <section className="section-pad-sm contact-faq">
        <div className="container">
          <h3 className="contact-faq__title">Frequently Asked Questions</h3>
          <div className="contact-faq__grid">
            {[
              { q: 'How quickly will you respond?', a: 'We typically respond within 2–4 hours. For urgent matters, please call us directly.' },
              { q: 'Do you charge for inquiries?', a: 'No. Submitting an inquiry is completely free. Charges only apply when a service is booked or confirmed.' },
              { q: 'Can foreign nationals use your services?', a: 'Absolutely. We specialize in serving international visitors, students, and business travellers in Mumbai bombay international worldwide from 100+ countries.' },
              { q: 'Do you operate on weekends and holidays?', a: 'Yes. Our team in Mumbai bombay international worldwide is available 24/7, including weekends and n national holidays.' },
            ].map((faq, i) => (
              <div key={i} className="contact-faq__item">
                <h4 className="contact-faq__q">{faq.q}</h4>
                <p className="contact-faq__a">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}